import type {RandomizedLoadingMessage} from '@Components/email-wizard/components/panel-overview-email-campaign/panel-overview-email-campaign.types';
import {
  AiEmailSubjectOptionHeading,
  AiEmailSubjectToneOption,
  AiEmailSubjectEmojiOption,
  AiEmailSubjectPurposeOption,
  EmailWizardFlowType,
} from '@Components/email-wizard/components/panel-overview-email-campaign/panel-overview-email-campaign.types';
import type {EmailCampaign} from '@Components/email-wizard/email-wizard.types';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import type {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box.types';
import type {
  ContextualMenuItem,
  ContextualMenuItemWithEmoji,
} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {
  ContextualMenuGroupType,
  ContextualMenuItemGraphicType,
} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {getDefaultAttachments} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {getRandomNumberInRange} from '@Utils/math.util';

const LOADING_MESSAGE_VARIANTS_COUNT = 6;

export const getRandomizedLoadingMessages = (): RandomizedLoadingMessage => {
  const variant = getRandomNumberInRange(1, LOADING_MESSAGE_VARIANTS_COUNT).toString();

  return {
    campaignName: window.i18next.t(`pmwjs_publishing_campaign_name_loading_${variant}`),
    subject: window.i18next.t(`pmwjs_publishing_subject_line_loading_${variant}`),
    previewText: window.i18next.t(`pmwjs_publishing_preview_text_loading_${variant}`),
  };
};

const EMOJI_REGEX = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)(\u200D(\p{Emoji_Presentation}|\p{Emoji}\uFE0F))*/u;

export const extractLoadingEmoji = (loadingMessage?: string): string | undefined => {
  if (!loadingMessage) {
    return undefined;
  }

  const match = EMOJI_REGEX.exec(loadingMessage);
  return match?.[0];
};

export const extractLoadingText = (loadingMessage?: string): string | undefined => {
  if (!loadingMessage) {
    return undefined;
  }

  const match = EMOJI_REGEX.exec(loadingMessage);
  return match ? loadingMessage.slice(match[0].length) : loadingMessage;
};

export interface EmailWizardFlowInfo {
  type: EmailWizardFlowType;
  entityId: string;
}

export const getEmailWizardFlowInfo = (emailCampaign: EmailCampaign): EmailWizardFlowInfo => {
  if (emailCampaign.posterHashedId && emailCampaign.pageHashedId) {
    return {type: EmailWizardFlowType.Poster, entityId: emailCampaign.posterHashedId};
  }

  if (emailCampaign.idEmailTemplate) {
    return {type: EmailWizardFlowType.Template, entityId: emailCampaign.idEmailTemplate};
  }

  return {type: EmailWizardFlowType.Campaign, entityId: emailCampaign.id};
};

export const INITIAL_PROMPT_BOX_CONFIG: AiPromptBox = {
  prompt: '',
  size: AiPromptBoxSize.SMALL_EXTENDED,
  placeholder: '',
  promptBoxState: PromptBoxState.IDLE,
  attachments: getDefaultAttachments(),
  contextualMenu: [
    {
      label: window.i18next.t('pmwjs_tone'),
      key: AiEmailSubjectOptionHeading.Tone,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {
          label: window.i18next.t('pmwjs_neutral'),
          value: AiEmailSubjectToneOption.Neutral,
          active: true,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '📘',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_professional'),
          value: AiEmailSubjectToneOption.Professional,
          active: false,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '💼',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_excited'),
          value: AiEmailSubjectToneOption.Excited,
          active: false,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '⚡',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_confident'),
          value: AiEmailSubjectToneOption.Confident,
          active: false,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🦁',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_witty'),
          value: AiEmailSubjectToneOption.Witty,
          active: false,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🧠',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_empowering'),
          value: AiEmailSubjectToneOption.Empowering,
          active: false,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '💪',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_funny'),
          value: AiEmailSubjectToneOption.Funny,
          active: false,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🤹',
        } as ContextualMenuItemWithEmoji,
      ],
    },
    {
      label: window.i18next.t('pmwjs_emojis'),
      key: AiEmailSubjectOptionHeading.Emojis,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {label: window.i18next.t('pmwjs_use_emojis'), value: AiEmailSubjectEmojiOption.UseEmoji, active: true} as ContextualMenuItem,
        {label: window.i18next.t('pmwjs_no_emojis'), value: AiEmailSubjectEmojiOption.NoEmoji, active: false} as ContextualMenuItem,
      ],
    },
    {
      label: window.i18next.t('pmwjs_purpose'),
      key: AiEmailSubjectOptionHeading.Purpose,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {label: window.i18next.t('pmwjs_generate_interest'), value: AiEmailSubjectPurposeOption.GenerateInterest, active: true} as ContextualMenuItem,
        {label: window.i18next.t('pmwjs_drive_action'), value: AiEmailSubjectPurposeOption.DriveAction, active: false} as ContextualMenuItem,
        {label: window.i18next.t('pmwjs_ask_a_question'), value: AiEmailSubjectPurposeOption.AskQuestion, active: false} as ContextualMenuItem,
      ],
    },
  ],
};
